import type { AiRuntimeSettings, AiSettingsSaveInput } from "./settings";

export interface AiGatewayClientOptions {
  supabaseUrl: string;
  farmId: string;
  getAccessToken: () => Promise<string> | string;
  fetchImpl?: typeof fetch;
}

export interface AiGatewaySettingsState {
  settings: AiRuntimeSettings;
  domainVersion: number;
}

export interface AiGatewayAdvice {
  status:
    | "ok"
    | "disabled"
    | "not_configured"
    | "permission_denied"
    | "invalid_settings"
    | "provider_unavailable"
    | "failed";
  runId?: string;
  provider?: string;
  model?: string;
  advice?: {
    summary: string;
    findings: string[];
    recommendations: Array<{
      text: string;
      evidenceRefs: string[];
      generalGuidance: boolean;
    }>;
    missingData: string[];
  };
  errorCode?: string;
}

interface GatewaySettingsRow {
  enabled: boolean;
  primary_provider: AiRuntimeSettings["primaryProvider"];
  primary_model: string;
  primary_base_url: string;
  primary_api_key_configured: boolean;
  fallback_enabled: boolean;
  fallback_provider: AiRuntimeSettings["fallbackProvider"];
  fallback_model: string;
  fallback_base_url: string;
  fallback_api_key_configured: boolean;
  free_only: boolean;
  domain_version: number;
}

interface GatewayResponse {
  status: string;
  settings?: GatewaySettingsRow | null;
  domainVersion?: number;
  errorCode?: string;
  currentVersion?: number | null;
  provider?: string;
  model?: string;
  runId?: string;
  advice?: AiGatewayAdvice["advice"];
}

function gatewaySettings(row: GatewaySettingsRow): AiGatewaySettingsState {
  return {
    domainVersion: row.domain_version,
    settings: {
      enabled: row.enabled,
      primaryProvider: row.primary_provider,
      primaryModel: row.primary_model,
      primaryBaseUrl: row.primary_base_url,
      primaryApiKeyConfigured: row.primary_api_key_configured,
      fallbackEnabled: row.fallback_enabled,
      fallbackProvider: row.fallback_provider,
      fallbackModel: row.fallback_model,
      fallbackBaseUrl: row.fallback_base_url,
      fallbackApiKeyConfigured: row.fallback_api_key_configured,
      freeOnly: row.free_only,
    },
  };
}

export class AiGatewayClient {
  readonly #supabaseUrl: string;
  readonly #farmId: string;
  readonly #getAccessToken: AiGatewayClientOptions["getAccessToken"];
  readonly #fetch: typeof fetch;

  constructor(options: AiGatewayClientOptions) {
    this.#supabaseUrl = options.supabaseUrl.replace(/\/$/, "");
    this.#farmId = options.farmId;
    this.#getAccessToken = options.getAccessToken;
    this.#fetch = options.fetchImpl ?? fetch;
  }

  async #request(body: Record<string, unknown>): Promise<GatewayResponse> {
    const token = await this.#getAccessToken();
    if (!token) throw new Error("AI_GATEWAY_ACCESS_TOKEN_REQUIRED");

    const response = await this.#fetch(`${this.#supabaseUrl}/functions/v1/ai-gateway`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...body, farmId: this.#farmId }),
    });

    const payload = (await response.json()) as GatewayResponse;
    if (!response.ok && !payload.status) {
      throw new Error("AI_GATEWAY_REQUEST_FAILED");
    }

    return payload;
  }

  async readSettings(): Promise<AiGatewaySettingsState> {
    const payload = await this.#request({ action: "settings.read" });
    if (payload.status !== "ok" || !payload.settings) {
      throw new Error(payload.errorCode ?? "AI_SETTINGS_READ_FAILED");
    }
    return gatewaySettings(payload.settings);
  }

  async saveSettings(input: AiSettingsSaveInput, expectedVersion: number): Promise<number> {
    const payload = await this.#request({
      action: "settings.save",
      expectedVersion,
      settings: input.settings,
      ...(input.primaryApiKey ? { primaryApiKey: input.primaryApiKey } : {}),
      ...(input.fallbackApiKey ? { fallbackApiKey: input.fallbackApiKey } : {}),
    });

    if (payload.status !== "ok" || typeof payload.domainVersion !== "number") {
      if (payload.status === "conflict") {
        throw new Error(`AI_SETTINGS_VERSION_CONFLICT:${String(payload.currentVersion ?? "")}`);
      }
      throw new Error(payload.errorCode ?? "AI_SETTINGS_SAVE_FAILED");
    }

    return payload.domainVersion;
  }

  async testProvider(): Promise<"ok" | "failed"> {
    const payload = await this.#request({ action: "provider.test" });
    return payload.status === "ok" ? "ok" : "failed";
  }

  async generateAdvice(input: {
    purpose: string;
    question?: string;
    lookbackDays?: number;
  }): Promise<AiGatewayAdvice> {
    const payload = await this.#request({
      action: "advice.generate",
      purpose: input.purpose,
      ...(input.question ? { question: input.question } : {}),
      ...(input.lookbackDays ? { lookbackDays: input.lookbackDays } : {}),
    });

    return {
      status: payload.status as AiGatewayAdvice["status"],
      ...(payload.runId ? { runId: payload.runId } : {}),
      ...(payload.provider ? { provider: payload.provider } : {}),
      ...(payload.model ? { model: payload.model } : {}),
      ...(payload.advice ? { advice: payload.advice } : {}),
      ...(payload.errorCode ? { errorCode: payload.errorCode } : {}),
    };
  }
}
